import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, GAME_TITLE, Palette, STAGE_BG_TEXTURE } from '@core/config/constants';
import { GROUP_NAME, findCharacter } from '@core/config/characters';
import { TextButton } from '@ui/components/TextButton';
import { MinigameRegistry } from '@core/registry/MinigameRegistry';
import type { MinigameDefinition } from '@core/types';

/** Ancho por encima del cual las tarjetas se muestran en una sola fila. */
const SINGLE_ROW_WIDTH = 1080;
const MAX_CARD_WIDTH = 300;

/**
 * Menu principal del juego. Lee el catalogo de minijuegos del registry y dibuja una
 * tarjeta por cada uno, reacomodando la grilla segun el tamaño de pantalla. Tocar una
 * tarjeta inicia la escena del minijuego.
 */
export class HubScene extends BaseScene {
  public constructor() {
    super(SceneKeys.Hub);
  }

  public create(): void {
    this.initResponsive();
    this.addBackground(STAGE_BG_TEXTURE);

    const { height } = this.scale;
    const titleY = height * 0.07;

    this.add
      .text(this.centerX, titleY, GAME_TITLE, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '58px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, titleY + 44, `con ${GROUP_NAME}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: '#4dd0e1',
      })
      .setOrigin(0.5);

    const navY = titleY + 100;
    new TextButton(this, {
      x: this.centerX,
      y: navY,
      label: '★ Personajes',
      width: 260,
      height: 60,
      color: Palette.secondary,
      onClick: () => this.scene.start(SceneKeys.CharacterSelect),
    });

    this.renderCoins();
    this.renderMinigameCards(MinigameRegistry.list(), navY + 60);
    this.renderSelectedCharacter();
  }

  private renderCoins(): void {
    this.add
      .text(this.scale.width - 28, 24, `⭐ ${this.profile.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);
  }

  private renderSelectedCharacter(): void {
    const { width, height } = this.scale;
    const character = findCharacter(this.profile.character);

    if (character === undefined) {
      this.add
        .text(this.centerX, height - 36, 'Tocá "★ Personajes" para elegir tu estrella ✨', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '20px',
          color: Palette.textLight,
          align: 'center',
          wordWrap: { width: width * 0.9 },
        })
        .setOrigin(0.5);
      return;
    }

    const avatarHeight = Math.min(150, height * 0.18);
    const avatar = this.add.image(this.centerX, height - avatarHeight / 2 - 36, character.texture).setOrigin(0.5);
    avatar.setScale(avatarHeight / avatar.height);

    this.add
      .text(this.centerX, height - 20, character.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);
  }

  private renderMinigameCards(minigames: MinigameDefinition[], gridTop: number): void {
    if (minigames.length === 0) {
      this.add
        .text(this.centerX, this.centerY, 'Pronto vas a tener minijuegos acá!', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '28px',
          color: Palette.textLight,
        })
        .setOrigin(0.5);
      return;
    }

    const { width, height } = this.scale;
    const footerReserved = Math.min(150, height * 0.18) + 70;
    const gridBottom = height - footerReserved;
    const gridAreaHeight = Math.max(160, gridBottom - gridTop);

    const columns = width >= SINGLE_ROW_WIDTH ? minigames.length : 2;
    const rows = Math.ceil(minigames.length / columns);
    const gap = Math.max(16, width * 0.025);

    const cardWidth = Math.min(MAX_CARD_WIDTH, (width * 0.92 - (columns - 1) * gap) / columns);
    const cardHeight = Math.min(cardWidth * 0.78, (gridAreaHeight - (rows - 1) * gap) / rows);

    const totalHeight = rows * cardHeight + (rows - 1) * gap;
    const startY = gridTop + (gridAreaHeight - totalHeight) / 2 + cardHeight / 2;

    minigames.forEach((minigame, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const cardsInRow = Math.min(columns, minigames.length - row * columns);
      const rowWidth = cardsInRow * cardWidth + (cardsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + cardWidth / 2;

      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);
      this.createCard(minigame, x, y, cardWidth, cardHeight);
    });
  }

  private createCard(
    minigame: MinigameDefinition,
    x: number,
    y: number,
    cardWidth: number,
    cardHeight: number,
  ): void {
    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(minigame.color, 1);
    background.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 22);

    const iconSize = Math.round(cardHeight * 0.34);
    const icon = this.add
      .text(0, -cardHeight * 0.14, minigame.icon, { fontSize: `${iconSize}px` })
      .setOrigin(0.5);

    const titleSize = Math.round(Math.min(26, cardWidth * 0.11));
    const title = this.add
      .text(0, cardHeight * 0.26, minigame.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: `${titleSize}px`,
        color: Palette.textLight,
        align: 'center',
        wordWrap: { width: cardWidth - 24 },
      })
      .setOrigin(0.5);

    container.add([background, icon, title]);
    container.setSize(cardWidth, cardHeight);
    container.setInteractive({ useHandCursor: true });

    container.on(Phaser.Input.Events.POINTER_OVER, () => container.setScale(1.05));
    container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
    container.on(Phaser.Input.Events.POINTER_UP, () => {
      this.audio.playClick();
      this.scene.start(minigame.key);
    });
  }
}
